import { Flex } from '@chakra-ui/react'
import { MessageSquareText, PhoneCall } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { useMessageGroupsViewModel } from '~/viewModels/Messaging/MessageGroupsViewModel'

type MissedCallActionsProps = {
  number: string
  isDisabled?: boolean
  missedCallId?: number
  onFollowUpStatusChanged?: (missedCallId: number, newStatus: string) => void
}

export const MissedCallActions = ({
  number,
  isDisabled = false,
  missedCallId,
  onFollowUpStatusChanged,
}: MissedCallActionsProps) => {
  const { t } = useTranslation()
  const {
    messagingModal: [, setMessagingModalState],
    callModal: [, setCallModalState],
  } = useModalState()
  const { userMessageGroups } = useMessageGroupsViewModel()
  const showTexting = useShowTexting()

  const hasMessageGroups = (userMessageGroups ?? []).length > 0

  const handleMessageClick = () => {
    if (!number || isDisabled) return

    // Optimistically update follow-up status to "Returned - SMS"
    if (missedCallId && onFollowUpStatusChanged) {
      onFollowUpStatusChanged(missedCallId, 'Returned - SMS')
    }

    setMessagingModalState({ open: true, number })
  }

  const handleCallClick = () => {
    if (!number || isDisabled) return

    // Optimistically update follow-up status to "Returned - Call back"
    if (missedCallId && onFollowUpStatusChanged) {
      onFollowUpStatusChanged(missedCallId, 'Returned - Call back')
    }

    setCallModalState({ open: true, callee: number })
  }

  return (
    <Flex align="center" gap="0.625rem">
      {showTexting && hasMessageGroups && (
        <QuickActionButton
          variant={QuickActionButtonVariants.Greyed_Out}
          tooltipText={
            isDisabled
              ? t('missedCalls.followUpStatus.assignUserFirst')
              : t('voicemail.tooltip.newText')
          }
          actionIcon={MessageSquareText}
          boxSize="1.25rem"
          iconProps={{
            boxSize: '1.25rem',
            color: 'fg.default',
            opacity: isDisabled ? 0.3 : 1,
          }}
          disabled={isDisabled}
          css={{
            backgroundColor: 'transparent',
            _hover: {
              backgroundColor: isDisabled ? 'transparent' : 'bg.subtle',
            },
            _active: {
              backgroundColor: isDisabled ? 'transparent' : 'bg.emphasized',
            },
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            pointerEvents: 'auto',
          }}
          onClick={handleMessageClick}
        />
      )}
      <QuickActionButton
        variant={QuickActionButtonVariants.Greyed_Out}
        tooltipText={
          isDisabled
            ? t('missedCalls.followUpStatus.assignUserFirst')
            : t('voicemail.tooltip.newCall')
        }
        actionIcon={PhoneCall}
        boxSize="1.25rem"
        iconProps={{
          boxSize: '1.25rem',
          color: 'fg.default',
          opacity: isDisabled ? 0.3 : 1,
        }}
        disabled={isDisabled}
        css={{
          backgroundColor: 'transparent',
          _hover: {
            backgroundColor: isDisabled ? 'transparent' : 'bg.subtle',
          },
          _active: {
            backgroundColor: isDisabled ? 'transparent' : 'bg.emphasized',
          },
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          pointerEvents: 'auto',
        }}
        onClick={handleCallClick}
      />
    </Flex>
  )
}
